import { Flex, Spinner } from '@chakra-ui/react'
import React, { useState } from 'react'
import iconOpenShare from '~/assets/icons/icon-open-share.svg?react'
import iconCopy from '~/assets/svgs/icon-copy.svg?react'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { useToastAlert } from '~/hooks/toast/useToastAlerts'
import { useClipboard } from '~/hooks/useClipboard'
import { textStyleKeys } from '~/themes/textStyles'
import { Size } from '~/types/ThemeTypes'

import { ApiError, VoicemailInstance } from '@mangovoice/types'

interface VoicemailTranscriptSectionProps {
  voicemail?: VoicemailInstance
  voicemailError?: ApiError
  isVoicemailLoading?: boolean
  onVoicemailClick?: () => void
  isToolTip?: boolean
}

export const VoicemailTranscriptSection = ({
  voicemail,
  voicemailError,
  isVoicemailLoading,
  onVoicemailClick,
}: VoicemailTranscriptSectionProps) => {
  const { toastError, toastSuccess } = useToastAlert()
  const { copyToClipboard } = useClipboard({
    onSuccess: () => toastSuccess('Voicemail transcript copied to clipboard.'),
    onFailure: () => toastError('Unable to copy voicemail transcript.'),
  })

  const [isHovered, setIsHovered] = useState(false)

  const handleCopyVoicemailTranscript = async () => {
    try {
      if (voicemail?.transcription) {
        copyToClipboard(voicemail.transcription)
      } else {
        toastError('No voicemail transcript to copy.')
      }
    } catch (error) {
      console.error('Failed to copy voicemail transcript:', error)
      toastError('Unable to copy the voicemail transcript. Please try again.')
    }
  }

  const renderVoicemailTranscript = () => {
    if (isVoicemailLoading) {
      return (
        <Flex align="center" mt="0.5rem">
          <Spinner size="sm" mr={2} />
          <Flex textColor="gray.1300" textStyle={textStyleKeys.Body2}>
            Loading voicemail transcript...
          </Flex>
        </Flex>
      )
    }

    if (voicemailError) {
      if (voicemailError.status === 403) {
        return (
          <Flex textColor="red.800" textStyle={textStyleKeys.Body1} mt="0.5rem">
            You do not have permission to access this voicemail. Contact your
            system administrator.
          </Flex>
        )
      } else if (voicemailError.status === 410) {
        return (
          <Flex
            textColor="gray.1300"
            textStyle={textStyleKeys.Body1}
            mt="0.5rem"
          >
            Voicemail has been deleted.
          </Flex>
        )
      } else {
        return (
          <Flex
            textColor="gray.1300"
            textStyle={textStyleKeys.Body1}
            mt="0.5rem"
          >
            Voicemail not available.
          </Flex>
        )
      }
    }

    if (voicemail?.transcription) {
      return (
        <Flex textColor="gray.1300" textStyle={textStyleKeys.Body1} mt="0.5rem">
          {voicemail.transcription}
        </Flex>
      )
    }

    return (
      <Flex textColor="gray.1300" textStyle={textStyleKeys.Body1} mt="0.5rem">
        Voicemail transcription not available.
      </Flex>
    )
  }

  return (
    <Flex
      flexDirection="column"
      position="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Flex
        textColor="gray.1300"
        textStyle={textStyleKeys.Body1_Bold}
        position="relative"
      >
        Voicemail
        {voicemail && (
          <Flex
            alignItems="center"
            className="quick-action-buttons"
            display={isHovered ? 'flex' : 'none'}
            position="absolute"
            right="0"
            gap="0.5rem"
          >
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={iconCopy}
              onClick={handleCopyVoicemailTranscript}
              tooltipText="Copy to clipboard"
            />
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={iconOpenShare}
              onClick={onVoicemailClick ?? (() => {})}
              tooltipText="Go to voicemail"
            />
          </Flex>
        )}
      </Flex>

      {renderVoicemailTranscript()}
    </Flex>
  )
}
